import { Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { GeoAPITypeEnum } from '@shared/models/geo-api-type.model';
import { ParkingSpotModel } from '@shared/models/parking-spot.model';
import { ParkingStatusFilterEnum, ParkingZoneLoadingFilterEnum } from '@shared/models/parking-zone.model';
import { GeoApiService } from '@shared/services/geo-api-service/geo-api.service';
import { ParkingService } from '@shared/services/layer-service/parking-service/parking.service';
import { MapLayers } from '@shared/utils/mappers/layers-map.model';
import { ButtonClassesEnum, IButtonModel, IconClassesEnum } from '@types-custom/models/ui/button-model';
import { Icon } from '@types-custom/models/ui/icon-model';
import { forkJoin, map } from 'rxjs';

import * as turf from '@turf/turf';
import { Feature, MultiPolygon } from 'geojson';
import { ParkingLoadingZoneModel } from '@shared/models/parking-loading-zone.model';

@Component({
  selector: 'cdm-manizales-map-filter-parking-loading',
  templateUrl: './map-filter-parking-loading.component.html',
  styleUrls: ['./map-filter-parking-loading.component.scss'],
})
export class MapFilterParkingLoadingComponent implements OnInit {

  readonly layer = MapLayers.ParkingZoneLoading;
  readonly DEFAULT_ZONES = ['forLoadUnload'];

  formGroup: UntypedFormGroup = new UntypedFormGroup({
    zones: new FormControl(''),
    status: new FormControl(''),
  });

  zoneList: { name: string, value: string, }[] = [
    {
      name: 'Zona 1',
      value: ParkingZoneLoadingFilterEnum.ZONA_ONE,
    },
    {
      name: 'Zona 2',
      value: ParkingZoneLoadingFilterEnum.ZONA_TWO,
    },
    {
      name: 'Zona 3',
      value: ParkingZoneLoadingFilterEnum.ZONA_THREE,
    },
  ];

  statusParkingList: { name: string, value: string, }[] = [
    {
      name: 'Disponible',
      value: ParkingStatusFilterEnum.Disponible,
    },
    {
      name: 'Ocupado',
      value: ParkingStatusFilterEnum.Ocupado,
    },
    {
      name: 'Indefinido',
      value: ParkingStatusFilterEnum.Indefinido,
    }
  ];

  Icon = Icon;
  buttonTag = 'APLICAR';
  buttonAttributes: IButtonModel = {};
  restorebuttonAttributes: IButtonModel = {};
  applybuttonAttributes: IButtonModel = {};
  // dropdownAttributes: IClassesAttributes = {};
  createButtonProps: IButtonModel = {
    label: 'APLICAR',
    classAttributes: {
      buttonClass: ButtonClassesEnum.primary_1_button,
      spanClass: IconClassesEnum.text_complementary_2,
    },
  };

  constructor(
    private geoApiService: GeoApiService,
    // private mapboxDataService: MapboxDataService,
    private parkingService: ParkingService
  ) { }

  ngOnInit() {
    this.generateButtonAttributes();
    this.parkingService.getFilter().subscribe((data) => {
      if (data?.source === this.layer) {
        const filter = data.filters ?? {};
        Object.keys(filter).forEach(k => {
          this.formGroup.get(k)?.patchValue(data.filters[k]);
        });
      }
    });
  }

  private generateButtonAttributes(): void {
    this.buttonAttributes = {
      label: this.buttonTag,
      postIcon: Icon.search_w,
      classAttributes: {
        buttonClass: ButtonClassesEnum.primary_1_button_filter,
      },
    };
    this.restorebuttonAttributes = {
      label: 'LIMPIAR',
      postIcon: undefined,
      classAttributes: {
        buttonClass: ButtonClassesEnum.filter_button,
        spanClass: 'text-weight-600 text-size-16 h-150 p-40-px mt-18'
      },
    };
    this.applybuttonAttributes = {
      label: 'BUSCAR',
      postIcon: undefined,
      classAttributes: {
        buttonClass: ButtonClassesEnum.primary_1_button_filter,
      },
    };
  }

  reloadData(response: any) {
    this.parkingService.linesFilterNotifier$.next(response);
    this.parkingService.setFilter(
      {
        source: this.layer,
        filters: this.formGroup.getRawValue()
      }
    ); document.querySelector<HTMLElement>('#Filtro').click();
    setTimeout(() => {
      document.querySelector<HTMLElement>('#Filtro').parentElement.classList.add('applied-filter');
    }, 10);
  }

  private getFormValues(groupName?: string) {
    const params = groupName
      ? this.formGroup.get(groupName).getRawValue()
      : this.formGroup.getRawValue();
    for (const key in params) {
      if (!params[key]) {
        delete params[key];
      }
    }
    params.zones = this.DEFAULT_ZONES;
    return params;
  }

  private loadData(params: { [key: string]: any }) {
    return forkJoin({
      dataZones: this.geoApiService.getParkingLoadingZoneList(GeoAPITypeEnum.ParkingLoadingZone, true),
      dataSpots: this.geoApiService.getParkingSpotListFilter(GeoAPITypeEnum.ParkingSpot, true, params),
    }).pipe(map(
      resp => {
        const selectedZones = this.formGroup.getRawValue().zones;
        const filteredZones = selectedZones
          ? resp.dataZones.filter(z => selectedZones.includes(z.classProperties.name))
          : resp.dataZones;
        const intoZone = this.spotsIntoZone(
          filteredZones,
          resp.dataSpots
        );
        const response = {
          baseLayer: this.layer,
          layers: [
            {
              layer: this.layer,
              data: filteredZones,
            },
            {
              layer: this.layer,
              data: intoZone,
            }
          ]
        }
        return response;
      }
    ));
  }

  private spotsIntoZone(zones: ParkingLoadingZoneModel[], spots: ParkingSpotModel[]) {
    const zoneSpots: ParkingSpotModel[] = [];
    spots.forEach(spot => {
      const p = turf.point(spot.geometry.coordinates as [number, number], spot.classProperties);
      const isInZone =
        zones.some(zone => {
          const zoneFeature = {
            type: "Feature",
            geometry: zone.geometry
          };
          return turf.booleanPointInPolygon(p, zoneFeature as unknown as Feature<MultiPolygon>);
        });

      if (isInZone) {
        zoneSpots.push(spot);
      }
    });
    return zoneSpots;
  }

  applyFilter() {
    const params = this.getFormValues();
    this.loadData(params).subscribe({
      next: (resp) => {
        this.reloadData(resp);
      },
    });
  }

  cleanFilter() {
    this.formGroup.reset();
    this.parkingService.setFilter(undefined);
    this.loadData({ zones: this.getFormValues().zones })
      .subscribe({
        next: (resp) => {
          this.reloadData(resp);
        },
      })
  }
}
